import React, { useState, useEffect } from 'react';
import { Check, X, Key, UserCog, Building,LogOut, AlertTriangle, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { authenticatedFetch } from '../utils/apiUtils';
import { clientLogger } from '../utils/logger';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [isEditingOrgs, setIsEditingOrgs] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState(new Set());
  const [modalMode, setModalMode] = useState(null); // password, status, orgs
  const [success, setSuccess] = useState(null);

  // Get user ID from localStorage for API requests
  const getUserId = () => {
    const userDataString = localStorage.getItem('userData');
    if (!userDataString) return null;
    
    try {
      const userData = JSON.parse(userDataString);
      return userData.id;
    } catch (e) {
      clientLogger.error('Failed to parse user data from localStorage', e);
      return null;
    }
  };

  // Load users
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userId = getUserId();
        if (!userId) {
          throw new Error('No user ID available');
        }
        
        clientLogger.debug('UserManagement: Loading users with user ID', { userId });
        
        const response = await authenticatedFetch(`/api/admin/users/all?userId=${userId}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'X-User-ID': userId
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch users: ${response.status} ${errorText}`);
        }
        
        const data = await response.json();
        setUsers(data);
      } catch (error) {
        console.error('Error loading users:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  // Load organizations 
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const userId = getUserId();
        if (!userId) {
          throw new Error('No user ID available');
        }
        
        clientLogger.debug('UserManagement: Loading organizations with user ID', { userId });
        
        const response = await authenticatedFetch(`/api/admin/organizations?userId=${userId}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'X-User-ID': userId
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch organizations: ${response.status} ${errorText}`);
        }
        
        const data = await response.json();
        setOrganizations(data);
      } catch (error) {
        console.error('Error loading organizations:', error);
      }
    };

    loadOrganizations();
  }, []);

  const handleClearSessions = async (userId) => {
    try {
        const adminId = getUserId();
        if (!adminId) {
            throw new Error('No admin user ID available');
        }
        
        const response = await authenticatedFetch(`/api/admin/users/${userId}/clear-sessions?userId=${adminId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-ID': adminId
            }
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to clear sessions: ${response.status} ${errorText}`);
        }
        
        setSuccess(`Sessions cleared for user`);
        setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
        console.error('Error clearing sessions:', error);
        setError(error.message);
    }
  };

  const generateLoginLink = async (userId) => {
    try {
        const adminId = getUserId();
        if (!adminId) {
            throw new Error('No admin user ID available');
        }
        
        const response = await authenticatedFetch(`/api/admin/users/${userId}/login-link?userId=${adminId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-ID': adminId
            }
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to generate login link: ${response.status} ${errorText}`);
        }
        
        const data = await response.json();
        // Copy link to clipboard
        navigator.clipboard.writeText(data.link);
        setSuccess('Login link copied to clipboard');
        setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
        console.error('Error generating login link:', error);
        setError(error.message);
    }
  };

  const updatePassword = async (userId, newPassword) => {
    try {
      const adminId = getUserId();
      if (!adminId) {
        throw new Error('No admin user ID available');
      }
      
      const response = await authenticatedFetch(`/api/admin/users/${userId}/password?userId=${adminId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-ID': adminId
        },
        body: JSON.stringify({ password: newPassword })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update password: ${response.status} ${errorText}`);
      }
      
      setModalMode(null);
      setSelectedUser(null);
      setSuccess('Password updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (error) {
      console.error('Error updating password:', error);
      setError(error.message);
    }
  };

  const toggleUserStatus = async (userId, isEnabled) => {
    try {
      const adminId = getUserId();
      if (!adminId) {
        throw new Error('No admin user ID available');
      }
      
      const response = await authenticatedFetch(`/api/admin/users/${userId}/status?userId=${adminId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-ID': adminId
        },
        body: JSON.stringify({ enabled: isEnabled })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update user status: ${response.status} ${errorText}`);
      }
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? {...user, enabled: isEnabled}
          : user
      ));
      
      setSuccess(`User ${isEnabled ? 'enabled' : 'disabled'} successfully`);
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (error) {
      console.error('Error updating user status:', error);
      setError(error.message);
    }
  };

  const updateOrganizations = async (userId, organizationIds) => {
    try {
      const adminId = getUserId();
      if (!adminId) {
        throw new Error('No admin user ID available');
      }
      
      const response = await authenticatedFetch(`/api/admin/users/${userId}/organizations?userId=${adminId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-ID': adminId
        },
        body: JSON.stringify({ organizationIds: Array.from(organizationIds) })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update organizations: ${response.status} ${errorText}`);
      }
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? {...user, organizations: Array.from(organizationIds)}
          : user
      ));
      
      setModalMode(null);
      setSuccess('User organizations updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (error) {
      console.error('Error updating organizations:', error);
      setError(error.message);
    }
  };

  const handleSystemDelete = async (userId, userName) => {
    try {
      const adminId = getUserId();
      if (!adminId) {
        throw new Error('No admin user ID available');
      }
      
      const response = await authenticatedFetch(`/api/admin/users/${userId}/system?userId=${adminId}`, {
        method: 'DELETE',
        headers: {
          'X-User-ID': adminId
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete user from system: ${response.status} ${errorText}`);
      }

      // Remove user from the local state
      setUsers(prev => prev.filter(user => user.id !== userId));
      setError(null);
      setSuccess(`${userName} has been completely removed from the system`);
    } catch (error) {
      console.error('Error deleting user from system:', error);
      setError(error.message || 'Failed to delete user from system');
      setSuccess(null);
    }
  };

  const renderUserRow = (user) => (
    <tr key={user.id} className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {user.name}
            </div>
            <div className="text-sm text-gray-500">
              {user.email}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
          ${user.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {user.enabled ? 'Active' : 'Disabled'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {user.organizations?.length || 0} organizations
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => generateLoginLink(user.id)}
            className="text-blue-600 hover:text-blue-900"
            title="Generate login link"
          >
            <Key className="w-5 h-5" />
          </button>
          <button
            onClick={() => {
              setSelectedUser(user);
              setModalMode('password');
            }}
            className="text-yellow-600 hover:text-yellow-900"
            title="Change password"
          >
            <UserCog className="w-5 h-5" />
          </button>
          <button
            onClick={() => toggleUserStatus(user.id, !user.enabled)}
            className={`${user.enabled ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
            title={user.enabled ? 'Disable user' : 'Enable user'}
          >
            {user.enabled ? <X className="w-5 h-5" /> : <Check className="w-5 h-5" />}
          </button>
          <button
            onClick={() => {
              setSelectedUser(user);
              setSelectedOrgs(new Set(user.organizations || []));
              setModalMode('orgs');
            }}
            className="text-indigo-600 hover:text-indigo-900"
            title="Manage organizations"
          >
            <Building className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleClearSessions(user.id)}
            className="text-orange-600 hover:text-orange-900"
            title="Clear all sessions"
          >
            <LogOut className="w-5 h-5" />
          </button>
          <button
            onClick={() => {
              if (confirm(`Are you sure you want to completely delete ${user.name} from the system? This will remove ALL their data including comments, responses, and organization memberships. This action CANNOT be undone.`)) {
                handleSystemDelete(user.id, user.name);
              }
            }}
            className="text-red-600 hover:text-red-900"
            title="Delete user from system"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </td>
    </tr>
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {error}
              </p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <Check className="h-5 w-5 text-green-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                {success}
              </p>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col">
            <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Organizations
                        </th>
                        <th scope="col" className="relative px-6 py-3">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map(renderUserRow)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Change Modal */}
      {modalMode === 'password' && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Change Password for {selectedUser.name}</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const password = e.target.password.value;
              updatePassword(selectedUser.id, password);
            }}>
              <input
                type="password"
                name="password"
                placeholder="New password"
                className="w-full p-2 border rounded mb-4"
                required
              />
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setModalMode(null);
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Organization Management Modal */}
      {modalMode === 'orgs' && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Manage Organizations for {selectedUser.name}</h3>
            <div className="max-h-64 overflow-y-auto mb-4">
              {organizations.map(org => (
                <label key={org.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selectedOrgs.has(org.id)}
                    onChange={(e) => {
                      const newSelected = new Set(selectedOrgs);
                      if (e.target.checked) {
                        newSelected.add(org.id);
                      } else {
                        newSelected.delete(org.id);
                      }
                      setSelectedOrgs(newSelected);
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">{org.name}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setModalMode(null);
                  setSelectedUser(null);
                  setSelectedOrgs(new Set());
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => updateOrganizations(selectedUser.id, selectedOrgs)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;